import { createListenerMiddleware } from '@reduxjs/toolkit'
import { nanoid } from 'nanoid'
import { AppDispatch, RootState } from '@/store/store'
import { UserFoldersActions } from './slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { ApiError } from '@/util/errors/api-error'
import { MattersActions } from '@/matter/store/slice'
import { MatterRead, MatterType, MatterViewerRole } from '@/matter/store/schemas'

// Import fetch functions
import { getRootFolder } from '../fetch/get-root-folder'
import { getFolder } from '../fetch/get-folder'
import { getFolderChildren } from '../fetch/get-folder-children'
import { restoreFolder as restoreFolderApi } from '../fetch/post-restore-folder'

export const userFoldersListenerMiddleware = createListenerMiddleware()
const startListener = userFoldersListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()
const inFlightFolderChildrenLoads = new Set<string>()
const queuedForcedFolderChildrenRefreshes = new Set<string>()

function getApiStatusCode(error: unknown): number | undefined {
  return error instanceof ApiError ? error.status : undefined
}

function isMissingOrDeniedStatus(statusCode: number | undefined): boolean {
  return statusCode === 403 || statusCode === 404
}

function getMatterFolderIds(state: RootState['userFoldersState'], matterId: string): string[] {
  return Object.values(state.folders)
    .filter((folderState) => folderState.data.matter_id === matterId)
    .map((folderState) => folderState.data.id)
}

function isMatterFilesMemberVisible(matter: MatterRead | undefined): boolean {
  if (!matter) return false
  if (matter.type !== MatterType.STANDARD && matter.type !== MatterType.SAMPLE) return false
  if (matter.viewer_role === MatterViewerRole.Owner) return true
  return matter.viewer_role === MatterViewerRole.Collaborator && matter.accepted_at !== null
}

// ============================================================================
// Fetch Root Folder
// ============================================================================

startListener({
  actionCreator: UserFoldersActions.fetchRootFolder,
  effect: async (action, listenerApi) => {
    const { matterId, force = false } = action.payload
    const requestId = nanoid()

    // Check if already loaded
    const state = listenerApi.getState().userFoldersState
    if (!force && (state.rootByMatterId[matterId] || state.rootFolderRequestsByMatterId[matterId]?.status === 'loading')) {
      return // Already have root folder
    }

    try {
      listenerApi.dispatch(UserFoldersActions.fetchRootFolderStarted({ matterId }))
      listenerApi.dispatch(
        UserFoldersActions.requestStarted({
          requestId,
          operationType: 'fetchRoot',
        })
      )

      const response = await getRootFolder(matterId)

      listenerApi.dispatch(UserFoldersActions.upsertFolder(response.folder))
      listenerApi.dispatch(UserFoldersActions.fetchRootFolderSucceeded({ matterId }))
      listenerApi.dispatch(UserFoldersActions.requestSucceeded({ requestId }))
      listenerApi.dispatch(UserFoldersActions.setInitialLoadComplete(true))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch root folder'
      const statusCode = getApiStatusCode(error)

      if (isMissingOrDeniedStatus(statusCode)) {
        const folderIds = getMatterFolderIds(listenerApi.getState().userFoldersState, matterId)
        listenerApi.dispatch(UserFilesActions.clearMatterFiles({ matterId, folderIds }))
        listenerApi.dispatch(UserFoldersActions.clearMatterFolders({ matterId }))
      }

      listenerApi.dispatch(UserFoldersActions.fetchRootFolderFailed({ matterId, error: errorMessage, statusCode }))
      listenerApi.dispatch(UserFoldersActions.requestFailed({ requestId, error: errorMessage }))
    }
  },
})

// ============================================================================
// Clear Matter Files state when matter-list access disappears
// ============================================================================

startListener({
  actionCreator: MattersActions.setMatters,
  effect: (action, listenerApi) => {
    const originalState = listenerApi.getOriginalState()
    const nextMatters = new Map(action.payload.map((matter) => [matter.id, matter]))

    Object.entries(originalState.mattersState.matters).forEach(([matterId, previousMatter]) => {
      if (!isMatterFilesMemberVisible(previousMatter)) return
      if (isMatterFilesMemberVisible(nextMatters.get(matterId))) return

      const folderIds = getMatterFolderIds(originalState.userFoldersState, matterId)
      listenerApi.dispatch(UserFilesActions.clearMatterFiles({ matterId, folderIds }))
      listenerApi.dispatch(UserFoldersActions.clearMatterFolders({ matterId }))
    })
  },
})

startListener({
  actionCreator: MattersActions.removeMatter,
  effect: (action, listenerApi) => {
    const originalState = listenerApi.getOriginalState()
    const { matterId } = action.payload
    const folderIds = getMatterFolderIds(originalState.userFoldersState, matterId)

    listenerApi.dispatch(UserFilesActions.clearMatterFiles({ matterId, folderIds }))
    listenerApi.dispatch(UserFoldersActions.clearMatterFolders({ matterId }))
  },
})

// ============================================================================
// Fetch Folder by ID
// ============================================================================

startListener({
  actionCreator: UserFoldersActions.fetchFolderById,
  effect: async (action, listenerApi) => {
    const { folderId } = action.payload
    const requestId = nanoid()

    try {
      listenerApi.dispatch(
        UserFoldersActions.requestStarted({
          requestId,
          operationType: 'fetchFolder',
          folderId,
        })
      )

      const folder = await getFolder(folderId)

      listenerApi.dispatch(UserFoldersActions.upsertFolder(folder))
      listenerApi.dispatch(UserFoldersActions.requestSucceeded({ requestId }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch folder'
      listenerApi.dispatch(UserFoldersActions.requestFailed({ requestId, error: errorMessage }))
    }
  },
})

// ============================================================================
// Fetch Folder Children
// ============================================================================

startListener({
  actionCreator: UserFoldersActions.fetchFolderChildren,
  effect: async (action, listenerApi) => {
    const { folderId, force = false } = action.payload
    const requestId = nanoid()
    const folderState = listenerApi.getState().userFoldersState.folders[folderId]

    if (inFlightFolderChildrenLoads.has(folderId)) {
      if (force) {
        queuedForcedFolderChildrenRefreshes.add(folderId)
      }
      return
    }

    // Check if already loaded
    if (!force && folderState?.childrenLoaded) {
      return // Already loaded children
    }

    inFlightFolderChildrenLoads.add(folderId)
    try {
      listenerApi.dispatch(
        UserFoldersActions.requestStarted({
          requestId,
          operationType: 'fetchChildren',
          folderId,
        })
      )

      const response = await getFolderChildren(folderId)

      listenerApi.dispatch(
        UserFoldersActions.setFolderChildren({
          parentId: folderId,
          children: response.folders,
        })
      )
      listenerApi.dispatch(UserFoldersActions.requestSucceeded({ requestId }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch folder children'
      const statusCode = getApiStatusCode(error)

      if (isMissingOrDeniedStatus(statusCode)) {
        listenerApi.dispatch(UserFilesActions.clearFolderFiles({ folderId }))
        listenerApi.dispatch(UserFoldersActions.clearFolderChildren({ folderId }))
        if (listenerApi.getState().userFoldersState.currentFolderId === folderId) {
          listenerApi.dispatch(UserFoldersActions.urlInitializationFailed({ folderId, error: errorMessage, statusCode }))
        }
      }

      listenerApi.dispatch(UserFoldersActions.requestFailed({ requestId, error: errorMessage }))
    } finally {
      inFlightFolderChildrenLoads.delete(folderId)

      if (queuedForcedFolderChildrenRefreshes.delete(folderId)) {
        listenerApi.dispatch(UserFoldersActions.fetchFolderChildren({ folderId, force: true }))
      }
    }
  },
})

// ============================================================================
// Restore Folder
// ============================================================================

startListener({
  actionCreator: UserFoldersActions.restoreFolder,
  effect: async (action, listenerApi) => {
    const { folderId } = action.payload
    const requestId = nanoid()

    try {
      listenerApi.dispatch(
        UserFoldersActions.requestStarted({
          requestId,
          operationType: 'restore',
          folderId,
        })
      )

      const restoredFolder = await restoreFolderApi(folderId)

      listenerApi.dispatch(UserFoldersActions.upsertFolder(restoredFolder))
      listenerApi.dispatch(UserFoldersActions.requestSucceeded({ requestId }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to restore folder'
      listenerApi.dispatch(UserFoldersActions.requestFailed({ requestId, error: errorMessage }))
    }
  },
})

// ============================================================================
// Initialize from URL
// ============================================================================

/**
 * Handles direct navigation to a folder via URL.
 * Fetches the folder, validates it belongs to active matter,
 * loads ancestor chain for breadcrumbs, and sets as current folder.
 */
startListener({
  actionCreator: UserFoldersActions.initializeFromUrl,
  effect: async (action, listenerApi) => {
    const { folderId } = action.payload
    const state = listenerApi.getState()
    const activeMatterId = state.mattersState.activeMatterId

    try {
      // 1. Fetch the target folder
      const folder = await getFolder(folderId)

      // 2. Validate folder belongs to active matter (if a specific matter is selected)
      if (activeMatterId && folder.matter_id !== activeMatterId) {
        throw new Error('Folder belongs to different matter')
      }

      // 3. Upsert the target folder
      listenerApi.dispatch(UserFoldersActions.upsertFolder(folder))

      // 4. Load ancestor chain for breadcrumbs
      let current = folder
      while (current.parent_id) {
        const existingParent = listenerApi.getState().userFoldersState.folders[current.parent_id]
        if (existingParent) break // Already have this ancestor

        const parent = await getFolder(current.parent_id)
        listenerApi.dispatch(UserFoldersActions.upsertFolder(parent))
        current = parent
      }

      // 5. Set as current folder and fetch children
      listenerApi.dispatch(UserFoldersActions.setCurrentFolder({ folderId }))
      listenerApi.dispatch(UserFoldersActions.fetchFolderChildren({ folderId }))
      listenerApi.dispatch(UserFoldersActions.urlInitializationComplete())
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load folder'
      listenerApi.dispatch(UserFoldersActions.urlInitializationFailed({ folderId, error: message, statusCode: getApiStatusCode(error) }))
    }
  },
})
